import React, { useState, useEffect, useCallback } from 'react';
import PrimaryNavigation from 'components/ui/PrimaryNavigation';
import Icon from 'components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import SpendingBarChart from './components/SpendingBarChart';
import CategoryDonut from './components/CategoryDonut';
import ComparisonTable from './components/ComparisonTable';
import PredictiveChart from './components/PredictiveChart';
import SavingsOpportunities from './components/SavingsOpportunities';
import AIInsightCards from './components/AIInsightCards';
import DateRangeFilter from './components/DateRangeFilter';
import MonthlyNarrative from './components/MonthlyNarrative';

const PEER_AVERAGES = {
  Groceries: 380, Rent: 1100, Transportation: 180, Entertainment: 120,
  Shopping: 160, Dining: 180, Healthcare: 90, Other: 130,
};

function getDateRange(range) {
  const now = new Date();
  const thisYear = now?.getFullYear();
  const thisMonth = now?.getMonth();

  if (range === 'this_month') {
    return {
      start: new Date(thisYear, thisMonth, 1)?.toISOString(),
      end: new Date(thisYear, thisMonth + 1, 0, 23, 59, 59)?.toISOString(),
      label: 'This Month',
    };
  }
  if (range === 'last_month') {
    return {
      start: new Date(thisYear, thisMonth - 1, 1)?.toISOString(),
      end: new Date(thisYear, thisMonth, 0, 23, 59, 59)?.toISOString(),
      label: 'Last Month',
    };
  }
  if (range === 'last_3_months') {
    return {
      start: new Date(thisYear, thisMonth - 3, 1)?.toISOString(),
      end: new Date(thisYear, thisMonth + 1, 0, 23, 59, 59)?.toISOString(),
      label: 'Last 3 Months',
    };
  }
  if (range === 'last_6_months') {
    return {
      start: new Date(thisYear, thisMonth - 6, 1)?.toISOString(),
      end: new Date(thisYear, thisMonth + 1, 0, 23, 59, 59)?.toISOString(),
      label: 'Last 6 Months',
    };
  }
  return {
    start: new Date(thisYear, thisMonth, 1)?.toISOString(),
    end: new Date(thisYear, thisMonth + 1, 0, 23, 59, 59)?.toISOString(),
    label: 'This Month',
  };
}

function buildPredictiveData(transactions) {
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0)?.getDate();
  const today = now?.getDate();

  const dailyTotals = {};
  transactions?.forEach((t) => {
    const day = new Date(t.date || t.created_at)?.getDate();
    dailyTotals[day] = (dailyTotals?.[day] || 0) + Math.abs(t?.amount || 0);
  });

  let cumulative = 0;
  const data = [];
  for (let d = 1; d <= daysInMonth; d++) {
    if (d <= today) {
      cumulative += dailyTotals?.[d] || 0;
      data?.push({ day: d, actual: Math.round(cumulative), projected: null });
    } else {
      data?.push({ day: d, actual: null, projected: null });
    }
  }

  // 7-day rolling average for projection
  const last7 = Object.entries(dailyTotals)?.filter(([d]) => Number(d) > today - 7 && Number(d) <= today)?.map(([, v]) => v);
  const avgDaily = last7?.length > 0 ? last7?.reduce((s, v) => s + v, 0) / last7?.length : 0;
  const projected = Math.round(cumulative + avgDaily * (daysInMonth - today));

  // Fill projected from today onward
  let projCumulative = cumulative;
  for (let d = today + 1; d <= daysInMonth; d++) {
    projCumulative += avgDaily;
    data[d - 1].projected = Math.round(projCumulative);
  }

  return { data, projected };
}

function exportCSV(comparisonData, dateLabel) {
  const headers = ['Category', 'Last Month ($)', 'This Month ($)', 'Change (%)'];
  const rows = comparisonData?.map((r) => {
    const change = r?.lastMonth > 0 ? (((r?.thisMonth - r?.lastMonth) / r?.lastMonth) * 100)?.toFixed(1) : '0';
    return [r?.category, r?.lastMonth, r?.thisMonth, change];
  });
  const csv = [headers, ...rows]?.map((r) => r?.join(','))?.join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `spending-report-${dateLabel?.replace(/\s+/g, '-')?.toLowerCase()}.csv`;
  a?.click();
  URL.revokeObjectURL(url);
}

export default function SpendingInsightsDashboard() {
  const { user } = useAuth();
  const [dateRange, setDateRange] = useState('this_month');
  const [loading, setLoading] = useState(true);
  const [barData, setBarData] = useState([]);
  const [donutData, setDonutData] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);
  const [predictiveData, setPredictiveData] = useState([]);
  const [projectedSpend, setProjectedSpend] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [totalSpent, setTotalSpent] = useState(0);
  const [totalBudget, setTotalBudget] = useState(3000);

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { start, end } = getDateRange(dateRange);
      const now = new Date();
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1)?.toISOString();
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59)?.toISOString();

      const [txRes, lastTxRes, budgetRes] = await Promise.all([
        supabase?.from('transactions')?.select('*')?.eq('user_id', user?.id)?.gte('date', start)?.lte('date', end),
        supabase?.from('transactions')?.select('*')?.eq('user_id', user?.id)?.gte('date', lastMonthStart)?.lte('date', lastMonthEnd),
        supabase?.from('budgets')?.select('*')?.eq('user_id', user?.id)?.eq('month', `${now?.getFullYear()}-${String(now?.getMonth() + 1)?.padStart(2, '0')}`),
      ]);

      const txs = txRes?.data || [];
      const lastTxs = lastTxRes?.data || [];
      const budgets = budgetRes?.data || [];

      // Aggregate by category
      const thisMonthByCategory = {};
      txs?.forEach((t) => {
        const cat = t?.category || 'Other';
        thisMonthByCategory[cat] = (thisMonthByCategory?.[cat] || 0) + Math.abs(t?.amount || 0);
      });

      const lastMonthByCategory = {};
      lastTxs?.forEach((t) => {
        const cat = t?.category || 'Other';
        lastMonthByCategory[cat] = (lastMonthByCategory?.[cat] || 0) + Math.abs(t?.amount || 0);
      });

      const allCategories = [...new Set([...Object.keys(thisMonthByCategory), ...Object.keys(lastMonthByCategory)])];

      const bar = allCategories?.map((cat) => ({
        category: cat,
        thisMonth: Math.round(thisMonthByCategory?.[cat] || 0),
        lastMonth: Math.round(lastMonthByCategory?.[cat] || 0),
      }));

      const donut = allCategories?.filter((cat) => (thisMonthByCategory?.[cat] || 0) > 0)?.map((cat) => ({ name: cat, value: Math.round(thisMonthByCategory?.[cat]) }));

      const comparison = allCategories?.map((cat) => ({
        category: cat,
        thisMonth: Math.round(thisMonthByCategory?.[cat] || 0),
        lastMonth: Math.round(lastMonthByCategory?.[cat] || 0),
      }));

      // Savings opportunities
      const opps = allCategories?.filter((cat) => PEER_AVERAGES?.[cat] && (thisMonthByCategory?.[cat] || 0) > PEER_AVERAGES?.[cat])?.map((cat) => ({
          category: cat,
          yourSpend: Math.round(thisMonthByCategory?.[cat]),
          avgSpend: PEER_AVERAGES?.[cat],
          overPct: ((thisMonthByCategory?.[cat] - PEER_AVERAGES?.[cat]) / PEER_AVERAGES?.[cat]) * 100,
          suggestion: `Reduce ${cat?.toLowerCase()} spending by $${Math.round(thisMonthByCategory?.[cat] - PEER_AVERAGES?.[cat])} to match similar users.`,
        }))?.sort((a, b) => b?.overPct - a?.overPct);

      const totalBudgetVal = budgets?.reduce((s, b) => s + (b?.allocated_amount || 0), 0) || 3000;
      const totalSpentVal = Object.values(thisMonthByCategory)?.reduce((s, v) => s + v, 0);

      // Predictive data (always use this month's transactions)
      const thisMonthTxs = txRes?.data || [];
      const { data: predData, projected } = buildPredictiveData(thisMonthTxs);

      setBarData(bar);
      setDonutData(donut);
      setComparisonData(comparison);
      setPredictiveData(predData);
      setProjectedSpend(projected);
      setOpportunities(opps);
      setTotalSpent(Math.round(totalSpentVal));
      setTotalBudget(totalBudgetVal);
    } catch (err) {
      console.error('Error loading insights:', err);
    } finally {
      setLoading(false);
    }
  }, [user, dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const { label: dateLabel } = getDateRange(dateRange);

  return (
    <div className="min-h-screen bg-[#0D1B2A]">
      <PrimaryNavigation />
      <main className="pt-6 pb-24 md:pb-8 px-4 md:px-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#F8FAFC] flex items-center gap-2">
              <Icon name="BarChart3" size={24} color="#00E5FF" />
              Spending Insights
            </h1>
            <p className="text-[#64748B] text-sm mt-1">AI-powered analytics and spending intelligence</p>
          </div>
          <div className="flex items-center gap-3">
            <DateRangeFilter selected={dateRange} onChange={setDateRange} />
            <button
              onClick={() => exportCSV(comparisonData, dateLabel)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[rgba(0,229,255,0.08)] border border-[rgba(0,229,255,0.2)] text-[#00E5FF] text-xs font-medium hover:bg-[rgba(0,229,255,0.15)] transition-colors"
            >
              <Icon name="Download" size={14} />
              Export CSV
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total Spent', value: `$${totalSpent?.toLocaleString()}`, icon: 'CreditCard', color: '#00E5FF' },
            { label: 'Budget', value: `$${totalBudget?.toLocaleString()}`, icon: 'Wallet', color: '#7B2FBE' },
            { label: 'Remaining', value: `$${Math.max(totalBudget - totalSpent, 0)?.toLocaleString()}`, icon: 'PiggyBank', color: totalSpent > totalBudget ? '#EF4444' : '#10B981' },
            { label: 'Projected EOM', value: projectedSpend ? `$${projectedSpend?.toLocaleString()}` : '—', icon: 'TrendingUp', color: projectedSpend > totalBudget ? '#EF4444' : '#F59E0B' },
          ]?.map((s) => (
            <div key={s?.label} className="bg-[rgba(255,255,255,0.04)] border border-[rgba(0,229,255,0.1)] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${s?.color}20` }}>
                  <Icon name={s?.icon} size={14} color={s?.color} />
                </div>
                <span className="text-xs text-[#64748B]">{s?.label}</span>
              </div>
              <p className="text-xl font-bold font-mono" style={{ color: s?.color }}>{s?.value}</p>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 rounded-full border-2 border-[#00E5FF] border-t-transparent animate-spin" />
              <p className="text-[#64748B] text-sm">Loading insights...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Monthly AI Narrative */}
            <MonthlyNarrative
              spendingData={comparisonData}
              totalSpent={totalSpent}
              totalBudget={totalBudget}
              dateLabel={dateLabel}
            />

            {/* Row 1: Bar Chart + Donut */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2">
                <SpendingBarChart data={barData} />
              </div>
              <CategoryDonut data={donutData} />
            </div>

            {/* Row 2: Comparison Table + Predictive */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <ComparisonTable data={comparisonData} />
              <PredictiveChart data={predictiveData} budget={totalBudget} projected={projectedSpend} />
            </div>

            {/* Row 3: Savings Opportunities + AI Insights */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <SavingsOpportunities opportunities={opportunities} spendingData={comparisonData} />
              <AIInsightCards spendingData={comparisonData} dateRange={dateLabel} totalBudget={totalBudget} totalSpent={totalSpent} />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
